import type { DraggableAttributes, DraggableSyntheticListeners } from '@dnd-kit/core';
import type { CrmBoardQuery, CrmDealDto, CrmDealStage } from '@upme/contracts';
import { Avatar, AvatarFallback, AvatarImage } from '@upme/ui/components/avatar';
import { Button } from '@upme/ui/components/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@upme/ui/components/dropdown-menu';
import { cn } from '@upme/ui/lib/utils';
import { crmContactDisplayName, deriveCrmCardRoles, instagramProfileUrl } from '@upme/view-logic';
import { Bot, Brain, CalendarDays, GripVertical, Link2, MoreHorizontal, Tags, User } from 'lucide-react';
import { formatDateTime } from '../../users/lib/format';
import { CRM_PARTNERSHIP_STATUS_LABEL_RU } from '../lib/partnership-status';
import { CRM_PRODUCT_LABEL_RU, CRM_STAGE_LABEL_RU, CRM_STAGE_ORDER } from '../lib/stages';
import { useMoveCrmDealStage } from '../mutations';
import { CrmMetricChip } from './crm-metric-chip';
import { CrmRoleTag } from './crm-role-tag';

type Props = {
  deal: CrmDealDto;
  filters: CrmBoardQuery;
  selected?: boolean;
  onSelect?: () => void;
  dragAttributes?: DraggableAttributes | undefined;
  dragListeners?: DraggableSyntheticListeners | undefined;
};

function formatMinor(amount: number, currency: string): string {
  const major = amount / 100;
  return `${major.toLocaleString('ru-RU', { maximumFractionDigits: 2 })} ${currency}`;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

function botMetricLabel(contact: CrmDealDto['contact']): string {
  if (contact.botBlockedAt) return 'блок';
  if (contact.botStartedAt) return 'Start';
  return 'нет';
}

function managerInterestsMetricLabel(contact: CrmDealDto['contact']): string {
  const s = contact.managerEventInterestsSummary;
  if (s.tagCount > 0) return String(s.tagCount);
  if (s.hasText) return '✎';
  return '—';
}

function cardContextLine(deal: CrmDealDto): string | null {
  if (deal.eventStartsAt) return `${deal.title} · ${formatDateTime(deal.eventStartsAt)}`;
  if (deal.productKind !== 'other' && deal.productKind !== 'event') {
    return deal.title;
  }
  return null;
}

export function CrmDealCard({ deal, filters, selected, onSelect, dragAttributes, dragListeners }: Props) {
  const move = useMoveCrmDealStage(filters);
  const otherStages = CRM_STAGE_ORDER.filter((s) => s !== deal.stage);
  const displayName = crmContactDisplayName(deal.contact);
  const roles = deriveCrmCardRoles(deal);
  const contextLine = cardContextLine(deal);
  const econ = deal.partnerEconomics;
  const isManyChat = deal.contact.inboundChannel === 'instagram_manychat';
  const igUrl = instagramProfileUrl(deal.contact.instagramUsername ?? null);

  function moveTo(stage: CrmDealStage) {
    move.mutate({ dealId: deal.id, stage, sourceKey: deal.sourceKey });
  }

  function handleActivate() {
    onSelect?.();
  }

  return (
    <article
      className={cn(
        'rounded-md border border-border/70 bg-background/40 transition-colors',
        selected ? 'border-primary/50 bg-muted/20' : 'hover:border-border',
      )}
    >
      <div className="flex items-start gap-2.5 p-2.5">
        <button
          type="button"
          className="-ml-0.5 shrink-0 touch-none rounded-md p-1.5 text-muted-foreground hover:bg-muted/60 hover:text-foreground"
          aria-label="Перетащить карточку"
          {...dragAttributes}
          {...dragListeners}
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="size-5 stroke-[1.5]" />
        </button>
        <button
          type="button"
          className="min-w-0 flex-1 text-left"
          onClick={handleActivate}
          aria-pressed={selected}
          aria-label={`${displayName}, сделка`}
        >
          <div className="flex items-start gap-2.5">
            <Avatar className="size-8 shrink-0">
              {deal.contact.photoUrl ? <AvatarImage src={deal.contact.photoUrl} alt="" /> : null}
              <AvatarFallback className="text-xs">{initials(displayName)}</AvatarFallback>
            </Avatar>
            <div className="min-w-0 flex-1">
              <p className="truncate text-lg font-medium leading-tight">{displayName}</p>
              {deal.contact.username ? (
                <p className="truncate text-sm text-muted-foreground">@{deal.contact.username}</p>
              ) : null}
              {isManyChat ? <p className="truncate text-sm text-primary/90">Instagram / ManyChat</p> : null}
              {isManyChat && deal.contact.instagramUsername && igUrl ? (
                <a
                  href={igUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="truncate text-sm text-muted-foreground underline-offset-2 hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  @{deal.contact.instagramUsername}
                </a>
              ) : null}
              {isManyChat && deal.contact.lastInquiryAt ? (
                <p className="truncate text-sm text-muted-foreground">
                  Обращение: {formatDateTime(deal.contact.lastInquiryAt)}
                </p>
              ) : null}
              {contextLine ? (
                <p className="mt-0.5 line-clamp-2 text-sm text-muted-foreground">{contextLine}</p>
              ) : null}
            </div>
          </div>
        </button>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="size-9 shrink-0"
              aria-label="Действия со сделкой"
              onClick={(e) => e.stopPropagation()}
            >
              <MoreHorizontal className="size-[18px] stroke-[1.5]" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
            <DropdownMenuLabel>Переместить в</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {otherStages.map((stage) => (
              <DropdownMenuItem key={stage} disabled={move.isPending} onSelect={() => moveTo(stage)}>
                {CRM_STAGE_LABEL_RU[stage]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {!isManyChat ? (
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 border-t border-border/50 px-2.5 py-2">
          <CrmMetricChip
            icon={User}
            label="Заполненность профиля"
            value={`${deal.contact.profileCompletenessPercent}%`}
          />
          <CrmMetricChip
            icon={Brain}
            label="Психологический тест (НТС)"
            value={deal.contact.psychTestComplete ? 'да' : 'нет'}
            muted={!deal.contact.psychTestComplete}
          />
          <CrmMetricChip icon={Bot} label="Бот Telegram" value={botMetricLabel(deal.contact)} />
          <CrmMetricChip icon={Link2} label="Связи" value={String(deal.contact.friendsCount)} />
          <CrmMetricChip
            icon={CalendarDays}
            label="Ивенты (посещено)"
            value={String(deal.contact.meetingsCount)}
          />
          <CrmMetricChip
            icon={Tags}
            label="Интересы менеджера (CRM)"
            value={managerInterestsMetricLabel(deal.contact)}
            muted={
              !deal.contact.managerEventInterestsSummary.hasText &&
              deal.contact.managerEventInterestsSummary.tagCount === 0
            }
          />
        </div>
      ) : null}

      <footer className="flex flex-wrap items-center gap-1 border-t border-border/50 px-2.5 py-2">
        {roles.map((role) => (
          <CrmRoleTag key={role.kind} role={role} contact={deal.contact} />
        ))}
        <span
          className="ml-auto text-[13px] text-muted-foreground"
          title={CRM_PARTNERSHIP_STATUS_LABEL_RU[deal.partnershipStatus]}
        >
          {CRM_PRODUCT_LABEL_RU[deal.productKind]}
        </span>
      </footer>

      {econ ? (
        <div className="border-t border-border/50 px-2.5 py-2 text-sm text-muted-foreground">
          <div>
            {econ.slotsCount} мест · {econ.commissionPercent}% · билет{' '}
            {formatMinor(econ.ticketPriceMinor, econ.currency)}
          </div>
          <div>
            {formatMinor(econ.bountyPerSeatMinor, econ.currency)} / место · пул{' '}
            {formatMinor(econ.poolMinor, econ.currency)}
          </div>
        </div>
      ) : null}
    </article>
  );
}
